/**
 * Created by razial on 27.10.2026
 */

const NewVersionChecker = function () {
    const data = cleverapps.dataLoader.load(DataLoader.TYPES.VERSION_CHECKER) || {};

    this.lastVersion = data.lastVersion;
    this.lastShown = data.lastShown || 0;
    this.dismissed = data.dismissed || 0;
    this.reloads = data.reloads || 0;
};

NewVersionChecker.prototype.save = function () {
    cleverapps.dataLoader.save(DataLoader.TYPES.VERSION_CHECKER, {
        lastVersion: this.lastVersion,
        lastShown: this.lastShown || undefined,
        dismissed: this.dismissed || undefined,
        reloads: this.reloads || undefined
    });
};

NewVersionChecker.prototype.isStoreApp = function () {
    return Boolean(connector.platform.isLatestVersionCheckSupported() && connector.utils.guessPlatformURL());
};

NewVersionChecker.prototype.hasNewVersion = function () {
    return Version.compare(this.latestVersion, cleverapps.config.version) > 0;
};

NewVersionChecker.prototype.check = function () {
    if (this.latestVersion || this.checking) {
        return;
    }

    const tester = cleverapps.config.debugMode || connector.platform.isTester();
    if (this.isStoreApp() && !tester) {
        return;
    }

    const interval = this.isStoreApp() && !tester ? NewVersionChecker.STORE_CHECK_INTERVAL : NewVersionChecker.CHECK_INTERVAL;
    if (this.lastCheck + interval > Date.now()) {
        return;
    }
    this.lastCheck = Date.now();
    this.checking = true;

    const onComplete = (version, error) => {
        this.checking = false;

        if (Version.compare(version, cleverapps.config.version) > 0) {
            this.latestVersion = version;
        }

        if (connector.platform.isTester()) {
            cleverapps.metrics.logEvent(cleverapps.EVENTS.SYSTEM.VERSION_CHECK, { itemId: version, error });
        }

        this.showUpgradeWindowIfPossible();
    };

    if (this.isStoreApp()) {
        if (connector.platform.isLatestVersionCheckSupported()) {
            connector.platform.checkLatestVersion((code, response) => {
                if (code === connector.CODE_SUCCEED) {
                    onComplete(response && response.version);
                } else {
                    onComplete(undefined, "failed");
                }
            });
            return;
        }

        cleverapps.RestClient.get(
            `/latestversion/${connector.info.source}`,
            undefined,
            (data) => onComplete(data && data.version),
            () => onComplete(undefined, "failed")
        );
        return;
    }

    cleverapps.RestClient.get(
        `version.json?_t=${Date.now()}`,
        undefined,
        (data) => onComplete(data && data.version),
        () => onComplete(undefined, "failed"),
        {
            fullPath: true,
            ignoreNoRest: true
        }
    );
};

NewVersionChecker.prototype.reset = function () {
    this.lastCheck = 0;
    this.latestVersion = undefined;
    this.checking = false;
    this.windowShown = false;

    this.lastShown = 0;
    this.dismissed = 0;
    this.reloads = 0;
    this.save();
};

NewVersionChecker.prototype.isReloadsLimitReached = function () {
    return this.reloads >= NewVersionChecker.RELOADS_LIMIT;
};

NewVersionChecker.prototype.showUpgradeWindowIfPossible = function () {
    if (this.windowShown) {
        return;
    }

    if (ErrorHandler.isOnlyHtmlWindowAvailable()) {
        return;
    }

    if (cleverapps.environment.isSceneWithPreview() || cleverapps.environment.isAtlasAnalyzerScene()) {
        return;
    }

    if (!this.hasNewVersion()) {
        return;
    }

    let trigger;

    if (this.isStoreApp()) {
        const location = cleverapps.meta.getSelectedLocation();
        if (location && location.isAllContentDone && location.isAllContentDone()) {
            trigger = NewVersionChecker.TRIGGER_ALL_DONE;
        } else {
            const latest = this.latestVersion;
            const current = cleverapps.config.version;

            const isOldBuild = Version.getMajor(latest) > Version.getMajor(current)
                || Version.getMinor(latest) - Version.getMinor(current) >= NewVersionChecker.OLD_BUILD_MINOR_GAP;

            if (isOldBuild) {
                const interval = this.dismissed >= NewVersionChecker.DISMISSALS_LIMIT ? NewVersionChecker.OLD_BUILD_INTERVAL_DISMISSED : NewVersionChecker.OLD_BUILD_INTERVAL;
                if (this.lastShown + interval < Date.now()) {
                    trigger = NewVersionChecker.TRIGGER_OLD_BUILD;
                }
            }
        }
    } else if (!this.isReloadsLimitReached()) {
        trigger = NewVersionChecker.TRIGGER_NEW_VERSION;
    }

    if (!trigger) {
        return;
    }

    cleverapps.focusManager.display({
        focus: "UpgradeAvailableWindow",
        stack: true,
        important: true,
        action: function (f) {
            cleverapps.newVersionChecker.showUpgradeWindow(f, trigger);
        }
    });
};

NewVersionChecker.prototype.showUpgradeWindow = function (f, trigger) {
    this.windowShown = true;

    this.lastShown = Date.now();
    this.save();

    const screen = cleverapps.environment.getScreen();
    cleverapps.metrics.logEvent(cleverapps.EVENTS.SYSTEM.UPGRADE_WINDOW, { itemId: screen, error: trigger });

    new UpgradeAvailableWindow({
        trigger,
        onUpdate: () => {
            this.reloads += 1;
            this.save();

            cleverapps.metrics.logEvent(cleverapps.EVENTS.SYSTEM.UPGRADE_WINDOW_UPDATE, { itemId: screen, error: trigger });

            if (this.isStoreApp()) {
                connector.platform.openUrl(connector.utils.guessPlatformURL());
            } else {
                ErrorHandler.reload();
            }
        },
        onDismiss: () => {
            cleverapps.metrics.logEvent(cleverapps.EVENTS.SYSTEM.UPGRADE_WINDOW_DISMISS, { itemId: screen, error: trigger });

            this.dismissed++;
            this.save();
        }
    });

    cleverapps.focusManager.onceNoWindowsListener = f;
};

NewVersionChecker.prototype.processVersionChange = function () {
    if (Version.compare(this.lastVersion, cleverapps.config.version) >= 0) {
        return;
    }

    if (this.reloads) {
        cleverapps.metrics.logEvent(cleverapps.EVENTS.DEBUG.UPGRADE_SUCCESS, { counter: this.reloads });
    }

    this.lastVersion = cleverapps.config.version;
    this.lastShown = 0;
    this.dismissed = 0;
    this.reloads = 0;
    this.save();
};

NewVersionChecker.CHECK_INTERVAL = cleverapps.parseInterval("1 minute");
NewVersionChecker.STORE_CHECK_INTERVAL = cleverapps.parseInterval("1 hour");
NewVersionChecker.RELOADS_LIMIT = 3;
NewVersionChecker.OLD_BUILD_MINOR_GAP = 6;
NewVersionChecker.OLD_BUILD_INTERVAL = cleverapps.parseInterval("3 days");
NewVersionChecker.OLD_BUILD_INTERVAL_DISMISSED = cleverapps.parseInterval("7 days");
NewVersionChecker.DISMISSALS_LIMIT = 5;

NewVersionChecker.TRIGGER_NEW_VERSION = "new_version";
NewVersionChecker.TRIGGER_INCOMPATIBLE_DATA = "incompatible_data";
NewVersionChecker.TRIGGER_UNKNOWN_LOCATION = "unknown_location";
NewVersionChecker.TRIGGER_ALL_DONE = "all_done";
NewVersionChecker.TRIGGER_OLD_BUILD = "old_build";

Placements.ENTRIES["newVersionCheck"] = {
    type: Placements.ONSHOW_OR_STARTUP,
    action: function () {
        cleverapps.newVersionChecker.check();
    }
};

Placements.ENTRIES["newVersion"] = {
    type: Placements.FREE_FOCUS,
    action: function () {
        cleverapps.newVersionChecker.showUpgradeWindowIfPossible();
    }
};